import { useEffect, useMemo, useRef, useState } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  type AthleteTargets,
  type TargetItem,
  type UnitsPreference,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { DisclosureCard } from "../primitives/DisclosureCard";
import { EditorTopBar } from "../primitives/EditorTopBar";
import { UnsavedChangesDialog } from "../primitives/UnsavedChangesDialog";
import { useUnsavedGuard } from "../primitives/useUnsavedGuard";
import type { LeaveGuard } from "./LeaveGuard";
import { useBreakpoint } from "../useBreakpoint";
import type { ProgressDeps } from "../Progress/types";
import { useExerciseNameBySlug } from "../Logger/exerciseName";
import { useSessionResultsFromDeps } from "../Progress/useSessionResults";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import { makeEditorStyles } from "../ProgramEditor/styles";
import { makeComposerStyles } from "../ProgramComposer/composerStyles";
import { makePlanStyles } from "../ProgramComposer/planComposerStyles";
import { WeeklyTargetsSection } from "../ProgramComposer/WeeklyTargetsSection";
import { TargetHistoryList } from "./TargetHistoryList";
import { resolveTargetsSaveErrorCopy } from "./targetsSaveErrorCopy";
import { useTargetsDoc } from "./useTargetsDoc";

// The weekly-target editor — the athlete's OWN targets (ME-3). ONE home now:
// the AthleteTargets doc, edited by you on Me or by a coach for an athlete
// (athleteSub). The old program-targets home retired with ME-3.
//
// A NAVIGATED surface (not a modal): the host swaps it in, OUTSIDE its
// ScrollView, so the <EditorTopBar> stays sticky above the editor's own scroll.
// It composes primitives — <EditorTopBar> (‹ back · Save) + <WeeklyTargetsSection
// defaultOpen> (the composer's targets band) + <TargetHistoryList> — over
// useTargetsDoc (the ETag-locked read/write). It is NOT the ComposerEditLayout
// shell: no spine, no master-detail, no exercise picker — just the targets.
//
// No program is in scope, so no meta and no prescription reference; the exercise
// pool is what the athlete has actually trained (a target on an exercise you've
// never done has nothing to score against).
//
// Reference numbers ("you average 10.2/week") come from the SAME session-results
// cache the host already warmed (getCached is synchronous), so they paint with
// the editor, not after a spinner — the reason the host owns `deps`.
//
// Save is gated until useTargetsDoc has read the ETag: without it a PATCH would
// 428. The lock is fast, so this is a brief, honest greyed-out Save, not a
// spinner.

/** Whose targets are being edited. */
export type TargetsEditorSource = {
  /** Absent = your own targets; a sub = a coach editing that athlete's targets
   *  (the same AthleteTargets doc, via ?athleteSub). */
  athleteSub?: string;
  onSaved?: (targets: AthleteTargets) => void;
};

export type TargetsEditorProps = {
  theme: UiTheme;
  /** Base apiFetch (NOT the athlete-scoped wrapper) — carries If-Match. */
  apiFetch: ProgramApiFetch;
  /** The reference session-results cache lives here (athlete-scoped for a coach). */
  deps: ProgressDeps;
  source: TargetsEditorSource;
  /** Author label for a history entry's `bySub` — the host knows who "you" are. */
  resolveAuthor: (bySub: string) => string;
  units: UnitsPreference;
  locale: string;
  /** Left nav affordance ("‹ Leonardo") + its action. */
  backLabel: string;
  onClose: () => void;
  /** TG-1 — hands the host the SAME dirty-gated back the header's ‹ uses, so
   *  a platform back (Android's hardware button, a web route change) can run
   *  the confirm instead of discarding behind the editor's back. Returns true
   *  when it consumed the event (the confirm is now on screen), false when
   *  there was nothing to protect and the host should proceed with its own
   *  close. The BackHandler itself stays in the mount — it is a platform seam,
   *  and packages/ui deliberately does not import one. */
  registerBackGuard?: (guard: LeaveGuard) => void;
};

// Save-error copy moved to targetsSaveErrorCopy.ts (file-size cap, prompt.md
// §Limiti di dimensione): see resolveTargetsSaveErrorCopy below.

const docItems = (doc: AthleteTargets): TargetItem[] => doc.items;

export function TargetsEditor({
  theme,
  apiFetch,
  deps,
  source,
  resolveAuthor,
  units,
  locale,
  backLabel,
  onClose,
  registerBackGuard,
}: TargetsEditorProps) {
  const { t } = useTranslation();
  const { wide } = useBreakpoint();
  const p = theme.palette;
  const cs = useMemo(() => makeComposerStyles(p), [p]);
  const ps = useMemo(() => makePlanStyles(p), [p]);
  const selectStyles = useMemo(() => makeEditorStyles(p), [p]);
  const styles = useMemo(() => makeStyles(theme), [theme]);

  // Same single-flight cache the host warmed → getCached() is synchronous.
  const { results, catalog } = useSessionResultsFromDeps(deps);

  // `source` is rebuilt by the host on every render; narrow athleteSub once so
  // the memos below key on the stable string (a coach editing this athlete).
  const athleteSub = source.athleteSub;

  const paths = useMemo(() => {
    const q = athleteSub ? `?athleteSub=${encodeURIComponent(athleteSub)}` : "";
    return {
      doc: `/api/me/targets${q}`,
      history: `/api/me/targets/history${q}`,
      patch: `/api/me/targets${q}`,
    };
  }, [athleteSub]);

  const { doc, targets, history, historyTruncated, loadFailed, saving, save } =
    useTargetsDoc<AthleteTargets>({ apiFetch, paths, itemsOf: docItems });

  // Nothing on screen to seed from, so seed from the read — ONCE: a background
  // reload after a save must not clobber what is being edited.
  const [items, setItems] = useState<TargetItem[]>([]);
  const seededRef = useRef(false);
  // Seed -> then `ready`: the guard freezes its baseline at the first ready
  // render, which must be the one where `items` already holds the seed.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seededRef.current || doc === null) return;
    seededRef.current = true;
    setItems(targets);
    setSeeded(true);
  }, [doc, targets]);

  // TG-1 / #76: back with unsaved edits confirms before discarding; a clean
  // (or already-saved) editor leaves straight through. Same hook as the
  // program/plan composers: baseline, hardware back and web tab guard.
  const { handleBack, markSaved, dialogProps } = useUnsavedGuard({
    value: items,
    ready: seeded,
    onLeave: onClose,
    registerBackGuard,
  });

  const [err, setErr] = useState<string | null>(null);

  // Resolve BY SLUG against the live catalog, with the write-time name as the
  // fallback when the catalog row is gone — the shared Progress resolver.
  const resolveExerciseName = useExerciseNameBySlug(catalog);

  // The exercises you can put a target on: the ones the athlete has actually
  // trained — a target on an exercise you've never done has nothing to score
  // against, and the whole catalog would be a scroll, not a choice. Reads
  // alphabetically (unlike a program's meaningful slot order).
  const exercisePool = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of results ?? []) {
      for (const er of r.result.exerciseResults) {
        if (!seen.has(er.exerciseSlug)) {
          seen.set(
            er.exerciseSlug,
            resolveExerciseName(er.exerciseSlug, er.exerciseName),
          );
        }
      }
    }
    return [...seen]
      .map(([slug, name]) => ({ slug, name }))
      .sort((a, b) => a.name.localeCompare(b.name, locale));
  }, [results, resolveExerciseName, locale]);

  // A sub in `source` means a coach is editing an ATHLETE's targets; its
  // absence means you're editing your own — the self path (ME-3a).
  const isSelf = !athleteSub;

  const handleSave = async () => {
    setErr(null);
    const saved = items;
    const res = await save(saved);
    if (!res.ok) {
      const { key, defaultValue } = resolveTargetsSaveErrorCopy(res.reason, isSelf);
      setErr(t(key, { defaultValue }));
      return;
    }
    markSaved(saved);
    source.onSaved?.(res.doc);
    onClose();
  };

  const historyTitle = t("coach.targets.historyTitle", {
    defaultValue: "Change history",
  });

  const targetsSection = (
    <WeeklyTargetsSection
      theme={theme}
      cs={cs}
      ps={ps}
      selectStyles={selectStyles}
      programExercises={exercisePool}
      sessionResults={results ?? undefined}
      exerciseCatalog={catalog ?? undefined}
      unitsPreference={units}
      items={items}
      onChange={setItems}
      defaultOpen
    />
  );

  const historyList = (
    <TargetHistoryList
      theme={theme}
      history={history}
      historyTruncated={historyTruncated}
      loadFailed={loadFailed}
      resolveAuthor={resolveAuthor}
      resolveExerciseName={resolveExerciseName}
      units={units}
      locale={locale}
    />
  );

  return (
    <View style={styles.wrap}>
      <EditorTopBar
        theme={theme}
        backLabel={backLabel}
        onBack={handleBack}
        title={t("me.targets.title", { defaultValue: "Goals" })}
        onSave={handleSave}
        busy={saving}
        saveDisabled={doc === null}
        error={
          err ??
          (loadFailed
            ? t("coach.targets.errLoad", {
                defaultValue: "Couldn't load these targets. Go back and retry.",
              })
            : null)
        }
      />

      <ScrollView
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
      >
        {/* MX-8's twin: `targetsSection` stays in the SAME two wrapping
            Views on both layouts, only their style follows `wide`, so a
            resize mid-edit no longer tears the form down. `narrowStack`
            replaces the gap the removed Fragment used to get for free from
            `content`'s own gap (now only one child reaches it). The history
            rail/card is presentation-only, so it still swaps shape freely. */}
        <View style={wide ? styles.wideRow : styles.narrowStack}>
          <View style={wide ? styles.wideMain : styles.plain}>{targetsSection}</View>
          <View style={wide ? styles.wideRail : styles.plain}>
            {wide ? (
              // Desktop console: the audit trail sits in a fixed right rail
              // (a View, never a nested ScrollView).
              <>
                <Text style={styles.historyTitle}>{historyTitle}</Text>
                {historyList}
              </>
            ) : (
              // Phone: the history folds away below the form (targets are the
              // point; the trail is on demand).
              <DisclosureCard
                theme={theme}
                accessibilityLabel={historyTitle}
                header={<Text style={styles.historyTitle}>{historyTitle}</Text>}
              >
                {historyList}
              </DisclosureCard>
            )}
          </View>
        </View>
      </ScrollView>

      <UnsavedChangesDialog theme={theme} {...dialogProps} />
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { flex: 1, backgroundColor: p.bg },
    content: { padding: s.s4, gap: s.s4, paddingBottom: 48 },
    // Desktop console: form left (takes the slack), audit trail in a fixed rail.
    wideRow: { flexDirection: "row", gap: s.s4, alignItems: "flex-start" },
    wideMain: { flex: 1, gap: s.s4 },
    wideRail: { width: 320, gap: s.s2 },
    // Narrow's twin of wideRow: the SAME gap the removed Fragment used to get
    // from `content`'s own gap (that container now has only one child).
    narrowStack: { gap: s.s4 },
    // Narrow's twin of wideMain/wideRail: no layout of its own, an explicit
    // no-op so the wrapper never passes `undefined` (consolidation.md: every
    // other wide/narrow wrapper in this fix names its neutral style too).
    plain: {},
    historyTitle: {
      color: p.ink,
      fontSize: ty.sizes.md,
      fontWeight: "700",
      paddingBottom: s.s1,
    },
  });
}
